import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { FiFolder, FiFile, FiCpu, FiCode, FiInfo } from 'react-icons/fi';
import { artifactoryService } from '../../services/artifactoryService';
import { NodeStatus } from '../../types/artifactory';

interface MiniFileTreeProps {
  manifestId: string;
  maxHeight?: string;
  onDependencyClick?: (nodeId: string) => void; // New prop for dependency navigation
}

export const MiniFileTree: React.FC<MiniFileTreeProps> = ({ 
  manifestId,
  maxHeight = '150px',
  onDependencyClick // New prop
}) => {
  const { darkMode } = useTheme();
  const [manifest, setManifest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'tree' | 'split'>('tree');
  
  useEffect(() => {
    const fetchManifest = async () => {
      try {
        if (!manifestId) {
          setError("No manifest ID provided");
          setLoading(false);
          return;
        }
        
        const data = await artifactoryService.getManifest(manifestId);
        setManifest(data);
      } catch (error) {
        console.error("Error fetching manifest:", error);
        setError("Failed to load project data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchManifest();
  }, [manifestId]);
  
  // Update selected node when selectedNodeId changes
  useEffect(() => {
    if (selectedNodeId && manifest && manifest.nodes) {
      setSelectedNode(manifest.nodes[selectedNodeId]);
    } else {
      setSelectedNode(null);
    }
  }, [selectedNodeId, manifest]);
  
  // Handle node selection
  const handleNodeSelect = (nodeId: string) => {
    setSelectedNodeId(prevId => prevId === nodeId ? null : nodeId);
    if (viewMode === 'tree') {
      setViewMode('split');
    }
  };
  
  // Toggle view mode
  const toggleViewMode = () => {
    setViewMode(prev => prev === 'tree' ? 'split' : 'tree');
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-2">
        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500 mr-2"></div>
        <span className="text-xs">Loading...</span>
      </div>
    );
  }
  
  if (error || !manifest) {
    return (
      <div className={`text-center py-2 text-xs ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
        {error || "No data available"}
      </div>
    );
  }
  
  // Get hydration stats
  const getHydrationStats = () => {
    const stats = {
      total: 0,
      fresh: 0,
      stale: 0,
      ghost: 0,
      hydrating: 0
    };
    
    Object.values(manifest.nodes).forEach((node: any) => {
      if (node.type === 'file') {
        stats.total++;
        stats[node.status]++;
      }
    });
    
    return stats;
  };
  
  const stats = getHydrationStats();
  const hydrationPercentage = stats.total > 0 
    ? Math.round((stats.fresh / stats.total) * 100) 
    : 0;
  
  // Build directory structure
  const buildDirStructure = () => {
    const dirStructure: any = { '/': { type: 'directory', children: {}, nodeId: null } };
    
    if (!manifest || !manifest.nodes) {
      return dirStructure;
    }
    
    // First pass: create all directories
    Object.values(manifest.nodes).forEach((node: any) => {
      if (!node || !node.path || typeof node.path !== 'string') {
        return;
      }
      
      try {
        const parts = node.path.split('/').filter(Boolean);
        let currentPath = '';
        let current = dirStructure['/'].children;
        
        // Create directory structure
        for (let i = 0; i < parts.length - 1; i++) {
          const part = parts[i];
          currentPath += '/' + part;
          
          if (!current[part]) {
            current[part] = { 
              type: 'directory', 
              children: {},
              path: currentPath,
              nodeId: null
            };
          }
          
          current = current[part].children;
        }
      } catch (error) {
        console.error("Error processing node for directory structure:", error, node);
      }
    });
    
    // Second pass: add files
    Object.values(manifest.nodes).forEach((node: any) => {
      if (!node || !node.path || typeof node.path !== 'string') return;
      
      try {
        const parts = node.path.split('/').filter(Boolean);
        
        if (parts.length === 0) return; // Skip empty paths
        
        if (node.type === 'directory') {
          // This is a directory node, find it in our structure and set its nodeId
          let current = dirStructure['/'].children;
          let currentPath = '';
          
          for (const part of parts) {
            currentPath += '/' + part;
            if (current[part]) {
              current[part].nodeId = node.id;
              current[part].status = node.status;
            }
            if (current[part] && current[part].children) {
              current = current[part].children;
            } else {
              break;
            }
          }
        } else {
          // This is a file node
          const fileName = parts[parts.length - 1];
          let current = dirStructure['/'].children;
          
          // Navigate to the parent directory
          for (let i = 0; i < parts.length - 1; i++) {
            const part = parts[i];
            if (!current[part]) {
              // Create the parent directory if it doesn't exist
              current[part] = { 
                type: 'directory', 
                children: {},
                path: '/' + parts.slice(0, i+1).join('/'),
                nodeId: null
              };
            }
            current = current[part].children;
          }
          
          // Add the file
          current[fileName] = { 
            type: 'file', 
            nodeId: node.id,
            status: node.status,
            path: node.path
          };
        }
      } catch (error) {
        console.error("Error processing node for file structure:", error, node);
      }
    });
    
    return dirStructure;
  };

  const dirStructure = buildDirStructure();
  
  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case NodeStatus.GHOST: return darkMode ? 'text-gray-500' : 'text-gray-400';
      case NodeStatus.PENDING: return 'text-yellow-500';
      case NodeStatus.HYDRATING: return 'text-blue-500';
      case NodeStatus.FRESH: return 'text-green-500';
      case NodeStatus.STALE: return 'text-orange-500';
      case NodeStatus.LOCKED: return 'text-red-500';
      default: return darkMode ? 'text-gray-500' : 'text-gray-400';
    }
  };
  
  // Recursive function to render tree
  const renderTree = (structure: any, path: string = '/', depth: number = 0) => {
    return Object.entries(structure).map(([name, info]: [string, any]) => {
      if (name === '/') {
        // Render root's children
        return (
          <div key="root">
            {Object.entries(info.children).map(([childName, childInfo]: [string, any]) => 
              renderNode(childName, childInfo, `/${childName}`, depth)
            )}
          </div>
        );
      } else {
        return renderNode(name, info, path + '/' + name, depth);
      }
    });
  };

  // Render a single node (file or directory)
  const renderNode = (name: string, info: any, fullPath: string, depth: number) => {
    const paddingLeft = `${depth * 8}px`;
    const isSelected = info.nodeId === selectedNodeId;
    
    if (info.type === 'file') {
      return (
        <div 
          key={info.nodeId || fullPath}
          className={`py-0.5 flex items-center cursor-pointer ${
            isSelected ? (darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-100') : ''
          } hover:${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
          style={{ paddingLeft }}
          onClick={() => handleNodeSelect(info.nodeId)}
        >
          <FiFile className={`mr-1 ${getStatusColor(info.status)}`} size={10} />
          <span className="truncate text-xs">{name}</span>
        </div>
      );
    } else {
      return (
        <div key={info.nodeId || fullPath}>
          <div 
            className={`py-0.5 flex items-center cursor-pointer ${
              isSelected ? (darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-100') : ''
            } hover:${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
            style={{ paddingLeft }}
            onClick={() => info.nodeId && handleNodeSelect(info.nodeId)}
          >
            <FiFolder className="mr-1 text-yellow-500" size={10} />
            <span className="font-medium truncate text-xs">{name}</span>
          </div>
          
          {info.children && Object.keys(info.children).length > 0 && (
            <div>
              {Object.entries(info.children).map(([childName, childInfo]: [string, any]) => 
                renderNode(childName, childInfo, `${fullPath}/${childName}`, depth + 1)
              )}
            </div>
          )}
        </div>
      );
    }
  };

  // Render blueprint details for selected node
  const renderBlueprintDetails = () => {
    if (!selectedNode) {
      return (
        <div className="flex items-center justify-center h-full">
          <p className="text-xs text-gray-500">Select a file to view details</p>
        </div>
      );
    }

    return (
      <div className="p-1 overflow-y-auto h-full text-xs">
        <div className="mb-1">
          <h3 className="font-semibold text-xs break-words">{selectedNode.path}</h3>
          <div className={`${getStatusColor(selectedNode.status)}`}>
            {selectedNode.status}
          </div>
        </div>
        
        {/* Persona */}
        <div className="mb-1">
          <div className="font-medium text-xs">Persona:</div>
          <div className={`px-1 py-0.5 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} flex items-center`}>
            <FiCpu className="mr-1" size={8} />
            <span className="truncate">{selectedNode.assigned_persona_id || 'None'}</span>
          </div>
        </div>
        
        {/* Dependencies - always show, even with 0 entries, and show all of them */}
        <div className="mb-1">
          <div className="font-medium text-xs">Dependencies: ({selectedNode.dependencies ? selectedNode.dependencies.length : 0})</div>
          <div className={`px-1 py-0.5 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            {selectedNode.dependencies && selectedNode.dependencies.length > 0 ? (
              selectedNode.dependencies.map((depId: string) => {
                const depNode = manifest.nodes[depId];
                return (
                  <div 
                    key={depId} 
                    className={`flex items-center mb-1 ${onDependencyClick && depNode ? 'cursor-pointer' : ''} ${
                      onDependencyClick && depNode ? (darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200') : ''
                    }`}
                    onClick={(e) => {
                      console.log("Dependency clicked:", depId, depNode?.path);
                      if (onDependencyClick && depNode) {
                        console.log("Calling onDependencyClick with:", depId);
                        e.stopPropagation(); // Prevent card flip or other parent handlers
                        onDependencyClick(depId);
                      } else {
                        console.log("onDependencyClick not available or depNode is null:", 
                          { hasCallback: !!onDependencyClick, hasDepNode: !!depNode });
                      }
                    }}
                    title={depNode && onDependencyClick ? `Click to navigate to ${depNode.path}` : undefined}
                  >
                    <FiFile className={`mr-1 ${depNode ? getStatusColor(depNode.status) : 'text-gray-400'}`} size={8} />
                    <span className="break-words">{depNode ? depNode.path : depId}</span>
                  </div>
                );
              })
            ) : (
              <div className="text-gray-500 text-xs italic">No dependencies</div>
            )}
          </div>
        </div>
        
        {/* Generation Prompt - truncated */}
        <div>
          <div className="font-medium text-xs">Prompt:</div>
          <div className={`px-1 py-0.5 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} max-h-16 overflow-y-auto`}>
            {selectedNode.generation_prompt 
              ? selectedNode.generation_prompt
              : 'No prompt defined'}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mini-file-tree">
      <div className="flex justify-between items-center mb-0.5">
        <div className="text-xs font-medium">Project Files</div>
        <div className="flex items-center">
          <div className="text-xs mr-1 text-gray-500">
            {hydrationPercentage}%
          </div>
          <button 
            onClick={toggleViewMode}
            className={`p-0.5 rounded text-xs ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
            title={viewMode === 'tree' ? 'Show details' : 'Hide details'}
          >
            {viewMode === 'tree' ? <FiInfo size={10} /> : <FiCode size={10} />}
          </button>
        </div>
      </div>
      
      {viewMode === 'tree' ? (
        <div className="overflow-y-auto" style={{ maxHeight: "220px" }}>
          {renderTree(dirStructure)}
        </div>
      ) : (
        <div className="flex" style={{ maxHeight: "220px" }}>
          <div className="w-1/2 overflow-y-auto border-r" style={{ borderColor: darkMode ? '#374151' : '#e5e7eb' }}>
            {renderTree(dirStructure)}
          </div>
          <div className="w-1/2 overflow-y-auto">
            {renderBlueprintDetails()}
          </div>
        </div>
      )}
      
      <div className="mt-1 mb-1 flex justify-between text-xs">
        <div className="flex items-center space-x-1">
          <span className={`inline-flex items-center px-1.5 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <span className="font-medium mr-1">{stats.total}</span>Files
          </span>
          <span className={`inline-flex items-center px-1.5 rounded ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-100'}`}>
            <span className="font-medium text-green-500 mr-1">{stats.fresh}</span>Fresh
          </span>
        </div>
        <div className="flex items-center space-x-1">
          <span className={`inline-flex items-center px-1.5 rounded ${darkMode ? 'bg-orange-900 bg-opacity-30' : 'bg-orange-100'}`}>
            <span className="font-medium text-orange-500 mr-1">{stats.stale}</span>Stale
          </span>
          <span className={`inline-flex items-center px-1.5 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <span className="font-medium text-gray-500 mr-1">{stats.ghost}</span>Ghost
          </span>
        </div>
      </div>
    </div>
  );
};
